import React from 'react';
import { situacaoDoPrazo } from '../../src/lib/prazoSolicitacaoVisual';
import { ehUrgenciaAberta } from './PrazoSolicitacao';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { Tarefa, Profile } from '../../types';
import { parseLocalDate } from '../../src/lib/utils';

interface SolicitacoesGanttProps {
  tarefas: Tarefa[]; // já filtradas para categoria === 'solicitacao'
  users: Profile[];
  currentDate: Date;
  onNavigate: (date: Date) => void;
  onTaskClick: (t: Tarefa) => void;
}

const priorityBarColor = (p: string) => {
  switch (p) {
    case 'urgencia': return 'bg-rose-600';
    case 'alta': return 'bg-red-500';
    case 'media': return 'bg-yellow-500';
    case 'baixa': return 'bg-green-500';
    default: return 'bg-gray-400';
  }
};

const BAR_ROW_HEIGHT = 28;

const SolicitacoesGantt: React.FC<SolicitacoesGanttProps> = ({ tarefas, users, currentDate, onNavigate, onTaskClick }) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  const daysInMonth = monthEnd.getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const today = new Date();

  const goPrevMonth = () => onNavigate(new Date(year, month - 1, 1));
  const goNextMonth = () => onNavigate(new Date(year, month + 1, 1));
  const goToday = () => onNavigate(new Date());

  const monthLabel = currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  // Ninguém pegou ainda (sem data_inicio) — fica na fila, sem barra no calendário
  const semResponsavel = tarefas.filter(t => !t.data_inicio);

  // Agrupa por responsável as solicitações cujo intervalo cruza o mês visível
  const porResponsavel = new Map<string, Tarefa[]>();
  tarefas.forEach(t => {
    if (!t.data_inicio || !t.responsavel_id) return;
    const inicio = parseLocalDate(t.data_inicio);
    const fim = t.vencimento ? parseLocalDate(t.vencimento) : inicio;
    if (fim < monthStart || inicio > monthEnd) return;
    const list = porResponsavel.get(t.responsavel_id) || [];
    list.push(t);
    porResponsavel.set(t.responsavel_id, list);
  });

  const rows = Array.from(porResponsavel.entries())
    .map(([responsavelId, items]) => ({
      user: users.find(u => u.id === responsavelId),
      responsavelId,
      items,
    }))
    .sort((a, b) => (a.user?.full_name || '').localeCompare(b.user?.full_name || ''));

  const gridTemplate = `220px repeat(${daysInMonth}, minmax(28px, 1fr))`;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex-1 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={goPrevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400">
            <ChevronLeft size={18} />
          </button>
          <button onClick={goToday} className="px-3 py-1 text-xs font-semibold rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Hoje
          </button>
          <button onClick={goNextMonth} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400">
            <ChevronRight size={18} />
          </button>
        </div>
        <h3 className="font-bold text-gray-800 dark:text-white capitalize">{monthLabel}</h3>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="min-w-max">
          <div className="grid sticky top-0 z-10 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700" style={{ gridTemplateColumns: gridTemplate }}>
            <div className="px-3 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 sticky left-0 bg-gray-50 dark:bg-gray-900/50">Colaborador</div>
            {days.map(d => {
              const date = new Date(year, month, d);
              const isToday = date.toDateString() === today.toDateString();
              return (
                <div key={d} className={`text-center py-2 text-[10px] font-semibold ${isToday ? 'text-primary' : 'text-gray-400 dark:text-gray-500'}`}>
                  {d}
                </div>
              );
            })}
          </div>

          {rows.length === 0 && semResponsavel.length === 0 && (
            <div className="p-8 text-center text-gray-400 dark:text-gray-500 text-sm">Nenhuma solicitação neste período.</div>
          )}

          {rows.map(({ user, responsavelId, items }) => {
            const rowHeight = Math.max(48, items.length * BAR_ROW_HEIGHT + 16);
            return (
              <div
                key={responsavelId}
                className="grid border-b border-gray-50 dark:border-gray-700/50"
                style={{ gridTemplateColumns: gridTemplate, height: rowHeight }}
              >
                <div className="px-3 py-2 flex items-center gap-2 sticky left-0 bg-white dark:bg-gray-800 border-r border-gray-100 dark:border-gray-700 z-[1]">
                  <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-400 shrink-0">
                    {(user?.full_name || '?').charAt(0).toUpperCase()}
                  </div>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">{user?.full_name || 'Desconhecido'}</span>
                </div>
                <div className="relative" style={{ gridColumn: `2 / span ${daysInMonth}`, height: rowHeight }}>
                  {items.map((t, idx) => {
                    const inicio = parseLocalDate(t.data_inicio!);
                    const fimReal = t.vencimento ? parseLocalDate(t.vencimento) : null;
                    const startDay = inicio < monthStart ? 1 : inicio.getDate();
                    const endDay = fimReal ? (fimReal > monthEnd ? daysInMonth : fimReal.getDate()) : startDay;
                    const span = Math.max(1, endDay - startDay + 1);
                    return (
                      <div
                        key={t.id}
                        onClick={() => onTaskClick(t)}
                        // #6356/#6357: tempo que falta no passar do mouse; urgência aberta com anel vermelho.
                        title={`${t.titulo} · ${situacaoDoPrazo(t, new Date()).texto}`}
                        className={`absolute h-6 rounded-md px-2 flex items-center text-[10px] font-semibold text-white truncate cursor-pointer hover:opacity-90 transition-opacity ${priorityBarColor(t.prioridade)} ${!fimReal ? 'border-2 border-dashed border-white/70' : ''} ${ehUrgenciaAberta(t) ? 'ring-2 ring-red-400 ring-offset-1 dark:ring-offset-gray-800' : ''}`}
                        style={{
                          left: `calc(${(startDay - 1) / daysInMonth * 100}% + 2px)`,
                          width: `calc(${span / daysInMonth * 100}% - 4px)`,
                          top: 8 + idx * BAR_ROW_HEIGHT,
                        }}
                      >
                        {t.titulo}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {semResponsavel.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 mt-2">
              <div className="px-4 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 flex items-center gap-2">
                <Users size={12} /> Fila (sem responsável) — {semResponsavel.length}
              </div>
              <div className="flex flex-wrap gap-2 p-3">
                {semResponsavel.map(t => (
                  <button
                    key={t.id}
                    onClick={() => onTaskClick(t)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold text-white truncate max-w-[220px] hover:opacity-90 transition-opacity ${priorityBarColor(t.prioridade)}`}
                    title={t.titulo}
                  >
                    {t.titulo}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SolicitacoesGantt;
